import React, { type JSX } from "react";
import ReactDOM from "react-dom/client";
import { notNullish } from "@opencast/appkit";

import "./i18n";
import { SettingsManager, Provider as SettingsProvider } from "./settings";
import { Opencast, Provider as OpencastProvider } from "./opencast";
import { userHasWebcam, sleep } from "./util";
import { Provider as StudioStateProvier } from "./studio-state";
import { VideoServerProvider } from "./context/video-server-context";
import type { VideoServerInterface } from "./video-server/video-server-interface";
import { ToastContextProvider } from "./context/toast-context";


// Load the rest of the application and try to fetch the settings file from the
// server.
const initialize = Promise.all([
  // Load rest of the application code
  import("./App.tsx").then(mod => mod.App),

  // Check for camera devices
  userHasWebcam(),

  // Load the settings and initialize Opencast
  SettingsManager.init().then(async (initResult: [SettingsManager, VideoServerInterface | null]) => {
    const [settingsManager, videoServer] = initResult;

    // We wait for at most 300ms for `updateUser` to return. In the vast
    // majority of cases, it should be done long before that timeout. We just
    // don't want to stall the loading of the app forever if the user is on slow
    // internet. The information is not actually needed for anything important
    // in the beginning. It's mostly for debugging at this point.
    const oc = new Opencast(settingsManager.settings().opencast, videoServer);
    await Promise.race([
      oc.refreshConnection(),
      sleep(300),
    ]);

    return [settingsManager, oc, videoServer] as [SettingsManager, Opencast, VideoServerInterface];
  }),
]);

const render = (body: JSX.Element) => {
  const reactRoot = ReactDOM.createRoot(notNullish(document.getElementById("root")));
  reactRoot.render(body);
};

// (No dev-only ResizeObserver suppression or wrappers are applied here.)
// During development the browser may emit a noisy, non-actionable
// "ResizeObserver loop completed with undelivered notifications." error
// which the webpack dev overlay surfaces as a blocking runtime error.
// Silently ignore that specific message in development so the overlay
// does not interrupt debugging flow.
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  const isResizeObserverLoopMessage = (msg?: unknown): boolean =>
    typeof msg === "string" && msg.includes("ResizeObserver loop completed with undelivered notifications");

  // Instrumentation: count suppressed messages so developers can verify
  // the filter is active in the browser console when reproducing the bug.
  let __roSuppressed = 0;

  // Catch error events in capture phase to run before the dev overlay.
  window.addEventListener("error", (ev: ErrorEvent) => {
    try {
      // Log all error messages in dev for diagnosis.
      console.debug("dev:error-capture", ev?.message, ev?.error?.stack);
      if (isResizeObserverLoopMessage(ev?.message)) {
        __roSuppressed += 1;
        console.debug("Suppressed ResizeObserver error", __roSuppressed);
        ev.preventDefault();
        try {
          ev.stopImmediatePropagation();
        } catch (_err) {
          // ignore
        }
        return;
      }
    } catch (_err) {
      // ignore
    }
  }, true);

  // Catch unhandled rejections for browser variations.
  window.addEventListener("unhandledrejection", (ev: PromiseRejectionEvent) => {
    try {
      const reason = ev?.reason;
      let msg: unknown;
      if (typeof reason === "string") {
        msg = reason;
      } else if (reason && typeof (reason as { message?: unknown }).message === "string") {
        msg = (reason as { message: string }).message;
      }
      console.debug("dev:unhandledrejection-capture", msg);
      if (isResizeObserverLoopMessage(msg)) {
        __roSuppressed += 1;
        console.debug("Suppressed ResizeObserver rejection", __roSuppressed);
        ev.preventDefault();
        try {
          ev.stopImmediatePropagation();
        } catch (_err) {
          // ignore
        }
      }
    } catch (_err) {
      // ignore
    }
  }, true);
}

// After the initialization is done, render to the root element.
initialize.then(
  ([App, hasWebcam, [settingsManager, opencast, videoServer]]) => {
    render(
      <React.StrictMode>
        <VideoServerProvider videoServer={videoServer}>
          <OpencastProvider initial={opencast}>
            <SettingsProvider settingsManager={settingsManager}>
              {/* Toasts are used by many parts of the app (including
                  `StudioState`) — ensure `ToastContextProvider` is mounted
                  above `StudioStateProvier` so studio-state can call
                  `toastCtx.addItems()` during device changes. */}
              <ToastContextProvider>
                <StudioStateProvier hasWebcam={hasWebcam}>
                  <App />
                </StudioStateProvier>
              </ToastContextProvider>
            </SettingsProvider>
          </OpencastProvider>
        </VideoServerProvider>
      </React.StrictMode>,
    );
  },

  // This error case is very unlikely to occur.
  e => render(<p>
    {`Fatal error while loading app: ${e.message}`}
    <br />
    This might be caused by a incorrect configuration by the system administrator.
  </p>),
);
